import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { STRUTTURA_CONFIG, getSaluto } from "../config/strutturaConfig";
import { canAccessFatturazioneElettronica } from "../utils/fatturazioneAccess";
import { canAccessImmobiliare } from "../utils/immobiliareAccess";
import {
  FileText,
  Bed,
  Users,
  Building2,
  UserCog,
  ShoppingCart,
  ChevronRight,
  ChevronLeft,
  Plus,
  Send,
  Clock,
  Bell,
  Check,
  Package,
  Shield,
  Coins,
  FolderOpen,
  AlertTriangle,
  ShieldCheck,
  BadgeCheck,
  Newspaper,
  Receipt,
  Eye,
  CalendarDays,
  TrendingDown,
  FileBox,
  Scale,
  ClipboardList,
  BookOpen,
  Edit,
  Trash2,
  SlidersHorizontal,
  GripVertical,
  LayoutGrid,
  Minus,
  RotateCcw,
  X,
  EyeOff,
} from "lucide-react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useDashboardPreferences } from "../hooks/useDashboardPreferences";
import DashboardCardItem from "../components/dashboard/DashboardCardItem";
import { CARDS_BY_ID } from "../config/dashboardCards";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import DashboardAdvanced from "../components/DashboardAdvanced";
import PanoramicaQuickActionsBar from "../components/dashboard/PanoramicaQuickActionsBar";
import ReleaseNotesBanner from "../components/ReleaseNotesBanner";
import PageHeader from "../components/PageHeader";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { productGroupId, countAdesioniByGroup } from "../lib/risparmio/gruppi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Key per localStorage per tracciare newsletter lette
const READ_NEWSLETTERS_KEY = "affidya_read_newsletters";

/** Contenitore più largo della dashboard (panoramica e controllo completo). */
const DASHBOARD_MAX_WIDTH_CLASS =
  "w-full max-w-[min(1824px,calc(100vw-1.25rem))] mx-auto";

// Key per la modalità dashboard (panoramica = vista semplificata, completo = avanzata)
const DASHBOARD_MODE_KEY = "affidya_dashboard_mode";
// Vista aggregata multi-struttura dentro "Controllo completo"
const DASHBOARD_AGGREGATE_CONTROLLO_KEY = "affidya_dashboard_aggregate_controllo";

/** Hero in alto a destra: una delle quattro illustrazioni scelta a caso alla visita. */
const DASHBOARD_HERO_IMAGES = [
  "/images/dashboard-hero-1.png",
  "/images/dashboard-hero-2.png",
  "/images/dashboard-hero-3.png",
  "/images/dashboard-hero-4.png",
];

// Mock gruppi di acquisto
const MOCK_GRUPPI_ACQUISTO = [
  { id: "1", nome: "Pannoloni Serenità 30 pz", prezzo: "€ 18,90", prezzoPromo: "€ 14,90", aderenti: 12, minimo: 15, attivo: false },
  { id: "2", nome: "Guanti in nitrile (100 pz)", prezzo: "€ 9,50", prezzoPromo: "€ 6,90", aderenti: 18, minimo: 15, attivo: true },
  { id: "3", nome: "Detersivo pavimenti 5L", prezzo: "€ 12,00", prezzoPromo: "€ 8,50", aderenti: 8, minimo: 20, attivo: false },
  { id: "4", nome: "Traverse letto 60x90 (30 pz)", prezzo: "€ 22,00", prezzoPromo: "€ 16,50", aderenti: 14, minimo: 15, attivo: false },
  { id: "5", nome: "Sapone liquido mani 5L", prezzo: "€ 8,00", prezzoPromo: "€ 5,90", aderenti: 20, minimo: 15, attivo: true },
];

// Calendario italiano - giorni della settimana
const GIORNI_SETTIMANA = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const GIORNI_SETTIMANA_SHORT = ["L", "M", "M", "G", "V", "S", "D"];

// Componente Calendario Italiano Compatto - Responsive
function CalendarioItaliano({ selectedDate, onSelectDate, attivitaDate }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  
  // Detect screen size
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Get current week days (for mobile view)
  const getWeekDays = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    // Adjust for Monday start (Italian calendar)
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    
    const weekDays = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(monday);
      day.setDate(monday.getDate() + i);
      weekDays.push(day);
    }
    return weekDays;
  };
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days = [];
    
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };
  
  const days = isMobile ? getWeekDays() : getDaysInMonth(currentMonth);
  const monthNames = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
  
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  
  const isSelected = (date) => {
    if (!date) return false;
    return date.toDateString() === selectedDate.toDateString();
  };
  
  const hasActivity = (date) => {
    if (!date) return false;
    return attivitaDate.some(d => d.toDateString() === date.toDateString());
  };
  
  const isSunday = (date) => {
    if (!date) return false;
    return date.getDay() === 0;
  };

  // Mobile: Vista settimana compatta
  if (isMobile) {
    const weekDays = getWeekDays();
    return (
      <div>
        {/* Header mobile */}
        <div className="text-center mb-2">
          <p className="text-white/80 text-xs">Settimana corrente</p>
        </div>
        
        {/* Griglia settimana */}
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map((date, i) => (
            <div key={i} className="text-center">
              <p className={`text-[9px] mb-1 ${i === 6 ? "text-red-300" : "text-white/50"}`}>
                {GIORNI_SETTIMANA_SHORT[i]}
              </p>
              <button
                onClick={() => onSelectDate(date)}
                className={`
                  w-8 h-8 rounded-full flex items-center justify-center text-xs transition-all mx-auto
                  ${isSelected(date) ? "bg-white text-[#4CAF50] font-bold" : ""}
                  ${isToday(date) && !isSelected(date) ? "bg-white/30 font-bold text-white ring-2 ring-white/50" : ""}
                  ${hasActivity(date) && !isSelected(date) ? "bg-white/20 text-white font-semibold" : ""}
                  ${isSunday(date) && !isSelected(date) && !hasActivity(date) ? "text-red-300" : ""}
                  ${!isSelected(date) && !isToday(date) && !hasActivity(date) && !isSunday(date) ? "text-white/80" : ""}
                `}
              >
                {date.getDate()}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  // Desktop/Tablet: Vista mese completa
  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <Button variant="ghost" size="icon" onClick={prevMonth} className="h-6 w-6 text-white hover:bg-white/20">
          <ChevronLeft className="w-3 h-3" />
        </Button>
        <h3 className="font-semibold text-white text-sm">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <Button variant="ghost" size="icon" onClick={nextMonth} className="h-6 w-6 text-white hover:bg-white/20">
          <ChevronRight className="w-3 h-3" />
        </Button>
      </div>
      
      {/* Giorni settimana */}
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {GIORNI_SETTIMANA.map((giorno, i) => (
          <div 
            key={giorno} 
            className={`text-center text-[10px] font-medium py-0.5 ${i === 6 ? "text-red-300" : "text-white/60"}`}
          >
            {giorno}
          </div>
        ))}
      </div>
      
      {/* Griglia giorni */}
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((date, i) => (
          <button
            key={i}
            onClick={() => date && onSelectDate(date)}
            disabled={!date}
            className={`
              aspect-square flex items-center justify-center text-xs rounded transition-all
              ${!date ? "invisible" : ""}
              ${isSelected(date) ? "bg-white text-[#4CAF50] font-bold" : ""}
              ${isToday(date) && !isSelected(date) ? "bg-white/30 font-bold text-white" : ""}
              ${hasActivity(date) && !isSelected(date) ? "bg-white/20 text-white font-semibold" : ""}
              ${isSunday(date) && !isSelected(date) && !hasActivity(date) ? "text-red-300" : ""}
              ${!isSelected(date) && !isToday(date) && !hasActivity(date) && !isSunday(date) ? "text-white/80 hover:bg-white/20" : ""}
            `}
          >
            {date?.getDate()}
          </button>
        ))}
      </div>
    </div>
  );
}

// Componente Calendario Widget
function CalendarioWidget({ selectedDate, attivitaFiltrate = [], allAttivita = [], onOpenFullCalendar }) {
  const today = new Date();
  const giorni = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
  const giorniBrevi = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  
  // Prossimi eventi (prossimi 7 giorni)
  const getEventiProssimiGiorni = () => {
    const eventi = [];
    for (let i = 1; i <= 7; i++) {
      const giorno = new Date(today);
      giorno.setDate(today.getDate() + i);
      const eventiGiorno = allAttivita.filter(a => 
        a.data.toDateString() === giorno.toDateString()
      );
      if (eventiGiorno.length > 0) {
        eventi.push({
          data: giorno,
          giorno: giorniBrevi[giorno.getDay()],
          numeroGiorno: giorno.getDate(),
          eventi: eventiGiorno
        });
      }
    }
    return eventi;
  };
  
  const prossimiEventi = getEventiProssimiGiorni();
  
  return (
    <button
      onClick={onOpenFullCalendar}
      className="w-full h-full text-left focus:outline-none"
      data-testid="calendario-widget-btn"
    >
      <div className="flex flex-col h-full">
        {/* Header con icona badge e data */}
        <div className="flex items-start justify-between mb-3">
          <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center">
            <CalendarDays className="w-7 h-7 text-white" />
          </div>

          <div className="text-right">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wide">
              {giorni[today.getDay()]}
            </div>
            <div className="text-3xl font-bold text-slate-900 leading-none mt-0.5">
              {today.getDate()}
            </div>
            <div className="text-[11px] font-semibold text-emerald-700">
              Aprile
            </div>
          </div>
        </div>

        {/* Eventi di oggi */}
        <div className="space-y-1 mb-2">
          {attivitaFiltrate.length === 0 ? (
            <div className="bg-white/80 rounded-lg px-2.5 py-2 text-center shadow-sm ring-1 ring-emerald-100">
              <p className="text-[11px] text-slate-500 font-medium">Nessun evento oggi</p>
            </div>
          ) : attivitaFiltrate.length === 1 ? (
            <div className="bg-white/80 backdrop-blur rounded-lg px-2.5 py-1.5 shadow-sm ring-1 ring-emerald-100">
              <p className="text-xs font-semibold text-slate-800 truncate">{attivitaFiltrate[0].titolo}</p>
              {attivitaFiltrate[0].ora && <p className="text-[11px] font-semibold text-emerald-600">{attivitaFiltrate[0].ora}</p>}
            </div>
          ) : (
            <div className="bg-emerald-100/70 rounded-lg px-2.5 py-1.5 ring-1 ring-emerald-200">
              <p className="text-[11px] font-semibold text-emerald-700">{attivitaFiltrate.length} eventi oggi</p>
            </div>
          )}
        </div>

        {/* Prossimi eventi (solo tablet/desktop) */}
        <div className="hidden sm:block border-t border-emerald-200/60 pt-2 mt-auto">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-[10px] text-slate-600 font-semibold uppercase tracking-wide">Prossimi eventi</p>
              {prossimiEventi.length > 0 ? (
                <div className="space-y-0.5 mt-0.5">
                  {prossimiEventi.slice(0, 2).map((giorno, idx) => (
                    <div key={idx} className="flex items-center gap-1.5 text-[10px]">
                      <span className="text-slate-700 font-semibold w-10">{giorno.giorno} {giorno.numeroGiorno}</span>
                      <span className="text-slate-600 truncate flex-1">{giorno.eventi[0]?.titolo}</span>
                      {giorno.eventi[0]?.ora && (
                        <span className="text-emerald-600 font-semibold">{giorno.eventi[0].ora}</span>
                      )}
                      {giorno.eventi.length > 1 && (
                        <span className="text-emerald-700/70 text-[9px]">+{giorno.eventi.length - 1}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[10px] text-slate-500 mt-0.5">Nessun evento in programma</p>
              )}
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0 ml-2" />
          </div>
        </div>
      </div>
    </button>
  );
}

// Modal Calendario Completo
function CalendarioModal({ 
  isOpen, 
  onClose, 
  selectedDate, 
  onDateSelect, 
  attivitaFiltrate = [], 
  allAttivita = [], 
  onAddAttivita,
  onEditAttivita,
  onDeleteAttivita,
  googleCalendarConnected,
  googleCalendarEmail,
  onConnectGoogle,
  onDisconnectGoogle
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const monthNames = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  const giorniBrevi = ["L", "M", "M", "G", "V", "S", "D"];
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
    return days;
  };

  const days = getDaysInMonth(currentMonth);
  const today = new Date();
  
  const isToday = (date) => date?.toDateString() === today.toDateString();
  const isSelected = (date) => date?.toDateString() === selectedDate.toDateString();
  const isSunday = (date) => date?.getDay() === 0;
  const hasActivity = (date) => {
    if (!date) return false;
    return allAttivita.some(a => a.data.toDateString() === date.toDateString());
  };

  const prevMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));

  if (!isOpen) return null;

  const googleSvg = (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideCloseButton className="max-w-4xl w-[min(100vw-1.5rem,56rem)] p-0 bg-white rounded-2xl overflow-hidden border-0 shadow-xl">
        <DialogTitle className="sr-only">Calendario Attività</DialogTitle>

        <div className="flex h-[480px] min-h-[480px]">

          {/* ── Colonna sinistra: calendario (più stretta per dare spazio agli eventi) ── */}
          <div className="w-[38%] min-w-[240px] flex-shrink-0 bg-gradient-to-br from-amber-50 to-yellow-100 flex flex-col">
            {/* Header mese */}
            <div className="px-4 py-3 bg-gradient-to-r from-amber-400 to-yellow-400">
              <div className="flex items-center justify-between">
                <button onClick={prevMonth} className="p-1.5 hover:bg-white/30 rounded-full transition-colors">
                  <ChevronLeft className="w-4 h-4 text-amber-900" />
                </button>
                <h2 className="text-sm font-bold text-amber-900">
                  {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </h2>
                <button onClick={nextMonth} className="p-1.5 hover:bg-white/30 rounded-full transition-colors">
                  <ChevronRight className="w-4 h-4 text-amber-900" />
                </button>
              </div>
            </div>

            {/* Griglia calendario */}
            <div className="p-4 flex-1">
              <div className="grid grid-cols-7 gap-0.5 mb-2">
                {giorniBrevi.map((g, i) => (
                  <div key={g + i} className={`text-center text-[11px] font-semibold ${i === 6 ? "text-red-500" : "text-amber-700"}`}>
                    {g}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {days.map((date, i) => (
                  <button
                    key={i}
                    onClick={() => date && onDateSelect(date)}
                    disabled={!date}
                    className={`
                      aspect-square flex flex-col items-center justify-center text-xs rounded-full transition-all relative
                      ${!date ? "invisible" : ""}
                      ${isSelected(date) ? "bg-amber-500 text-white font-bold shadow-md" : ""}
                      ${isToday(date) && !isSelected(date) ? "bg-amber-200 text-amber-900 font-bold ring-2 ring-amber-400" : ""}
                      ${isSunday(date) && !isSelected(date) && !isToday(date) ? "text-red-400" : ""}
                      ${!isSelected(date) && !isToday(date) && !isSunday(date) ? "text-amber-900 hover:bg-amber-200/50" : ""}
                    `}
                  >
                    {date?.getDate()}
                    {hasActivity(date) && !isSelected(date) && (
                      <div className="absolute bottom-0.5 w-1 h-1 bg-amber-600 rounded-full" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Google Calendar */}
            <div className="px-4 pb-4">
              {googleCalendarConnected ? (
                <div className="flex items-center justify-between bg-white/70 rounded-xl p-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-white rounded-full flex items-center justify-center shadow-sm">
                      {googleSvg}
                    </div>
                    <div>
                      <p className="text-xs font-medium text-green-800">Collegato</p>
                      <p className="text-[10px] text-green-600 truncate max-w-[120px]">{googleCalendarEmail}</p>
                    </div>
                  </div>
                  <button onClick={onDisconnectGoogle} className="text-[10px] text-red-500 hover:text-red-700 font-medium" data-testid="btn-disconnect-google">
                    Scollega
                  </button>
                </div>
              ) : (
                <div className="w-full flex items-center justify-center gap-2 bg-white/50 border border-amber-200 rounded-xl p-2.5 opacity-60 cursor-not-allowed" data-testid="btn-connect-google">
                  {googleSvg}
                  <span className="text-xs font-medium text-slate-500">Google Calendar - Presto</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Colonna destra: eventi (area principale) ── */}
          <div className="flex-1 flex flex-col border-l border-slate-100 min-h-0 min-w-0">
            {/* Header eventi */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 flex-shrink-0">
              <div className="min-w-0 pr-2">
                <h3 className="text-sm font-bold text-slate-800 capitalize leading-tight">
                  {selectedDate.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                </h3>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  {attivitaFiltrate.length === 0 ? "Nessun evento" : `${attivitaFiltrate.length} ${attivitaFiltrate.length === 1 ? "evento" : "eventi"}`}
                </p>
              </div>
              <button
                onClick={onAddAttivita}
                className="p-1.5 bg-amber-500 hover:bg-amber-600 rounded-full transition-colors flex-shrink-0"
              >
                <Plus className="w-4 h-4 text-white" />
              </button>
            </div>

            {/* Lista eventi — scrollabile */}
            <div className="flex-1 overflow-y-auto min-h-0 p-4 sm:p-5 space-y-3">
              {attivitaFiltrate.length > 0 ? (
                attivitaFiltrate.map((att, idx) => (
                  <div key={idx} className={`bg-slate-50 rounded-xl p-4 border border-slate-100 ${att.source === 'google' ? 'border-l-4 border-l-blue-500' : ''} ${att.source === 'compleanno' ? 'border-l-4 border-l-pink-400' : ''}`}>
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-800 break-words leading-snug">{att.titolo}</p>
                        <p className={`text-xs mt-0.5 ${att.source === 'compleanno' ? 'text-pink-600' : 'text-amber-600'}`}>
                          {att.source === 'compleanno' ? '🎂 Ricorrenza — dati fascicolo' : `🕐 ${att.ora || 'Orario non specificato'}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
                        {att.source === 'google' ? (
                          <span className="text-[10px] text-blue-500 bg-blue-50 px-2 py-0.5 rounded-full">Google</span>
                        ) : att.source === 'compleanno' ? (
                          <span className="text-[10px] text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full">Compleanno</span>
                        ) : (
                          <>
                            <button onClick={() => onEditAttivita(att)} className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Modifica" data-testid={`btn-edit-attivita-${att.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => onDeleteAttivita(att.id)} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Elimina" data-testid={`btn-delete-attivita-${att.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                  <Clock className="w-9 h-9 text-slate-200 mb-2" />
                  <p className="text-sm text-slate-400">Nessun evento per questo giorno</p>
                  <button onClick={onAddAttivita} className="mt-3 text-xs font-semibold text-amber-600 hover:text-amber-700">
                    + Aggiungi evento
                  </button>
                </div>
              )}
            </div>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// SortableDashboardCard — wraps DashboardCardItem with dnd-kit sortable logic
// and edit-mode overlays (drag handle, visibility checkbox, size toggle).
// ---------------------------------------------------------------------------
function SortableDashboardCard({ card, cardData, isEditMode, onToggleVisible, onToggleSize }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.id });
  const cardMeta = CARDS_BY_ID[card.id];

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
  };

  if (!isEditMode) {
    if (!card.visible) return null;
    return (
      <div ref={setNodeRef} style={style} className="h-full min-h-0">
        <DashboardCardItem cardId={card.id} size={card.size} cardData={cardData} />
      </div>
    );
  }

  // Edit mode: overlay with handle, checkbox, size toggle
  return (
    <div ref={setNodeRef} style={style} className="relative group h-full min-h-0">
      {/* Dim overlay when hidden */}
      {!card.visible && (
        <div className="absolute inset-0 bg-white/70 backdrop-blur-sm rounded-xl z-10 pointer-events-none" />
      )}

      {/* Card content (non-interactive in edit mode) */}
      <div className="pointer-events-none select-none">
        <DashboardCardItem cardId={card.id} size={card.size} cardData={cardData} />
      </div>

      {/* Edit controls bar */}
      <div className="absolute inset-x-0 bottom-0 z-20 flex items-center justify-between gap-1 px-2 py-1.5 bg-black/60 backdrop-blur rounded-b-xl">
        {/* Drag handle */}
        <button
          className="touch-none text-white/70 hover:text-white cursor-grab active:cursor-grabbing p-1 rounded"
          {...attributes}
          {...listeners}
          title="Trascina per riordinare"
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Card label */}
        <span className="text-white/80 text-[10px] font-medium truncate flex-1 text-center">
          {cardMeta?.label || card.id}
        </span>

        {/* Size toggle */}
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => onToggleSize("detailed")}
            title="Vista dettagliata"
            className={`p-1 rounded text-[10px] transition-all ${card.size === "detailed" ? "text-emerald-300" : "text-white/40 hover:text-white/70"}`}
          >
            <LayoutGrid className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onToggleSize("compact")}
            title="Vista compatta"
            className={`p-1 rounded text-[10px] transition-all ${card.size === "compact" ? "text-emerald-300" : "text-white/40 hover:text-white/70"}`}
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Visibility toggle */}
        <button
          onClick={onToggleVisible}
          title={card.visible ? "Nascondi" : "Mostra"}
          className={`p-1 rounded transition-all ${card.visible ? "text-emerald-300 hover:text-white" : "text-white/40 hover:text-white/70"}`}
        >
          {card.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, token, activeStruttura, strutture } = useAuth();
  const strutturaKey = activeStruttura?.id ?? user?.struttura_id ?? "";
  // Nome struttura da contesto (si aggiorna con refreshUser), non solo da STRUTTURA_CONFIG mutabile
  const strutturaDisplayName =
    (activeStruttura?.name && String(activeStruttura.name).trim()) ||
    (user?.facility_name && String(user.facility_name).trim()) ||
    STRUTTURA_CONFIG.nome;
  const dashboardFatturazione = canAccessFatturazioneElettronica(user);
  const dashboardImmobiliare = canAccessImmobiliare(user);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const selectedMonthKey = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}`;
  const [attivita, setAttivita] = useState([]);

  // Modalità dashboard: 'panoramica' (semplificata) | 'completo' (avanzata)
  const [dashboardMode, setDashboardMode] = useState(() => {
    return localStorage.getItem(DASHBOARD_MODE_KEY) || "panoramica";
  });
  const handleToggleDashboardMode = (checked) => {
    const next = checked ? "completo" : "panoramica";
    setDashboardMode(next);
    localStorage.setItem(DASHBOARD_MODE_KEY, next);
    if (!checked) setAggregateControlloView(false);
    if (checked && isEditMode) cancelEditMode();
  };
  const isAdvancedMode = dashboardMode === "completo";

  // Dashboard personalisation
  const { prefs, setPrefs, save: savePrefs, reset: resetPrefs } = useDashboardPreferences(token);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editPrefs, setEditPrefs] = useState(null); // clone used while editing

  const enterEditMode = useCallback(() => {
    setEditPrefs(JSON.parse(JSON.stringify(prefs)));
    setIsEditMode(true);
  }, [prefs]);

  const cancelEditMode = useCallback(() => {
    setEditPrefs(null);
    setIsEditMode(false);
  }, []);

  const confirmEditMode = useCallback(async () => {
    try {
      await savePrefs(editPrefs);
      setIsEditMode(false);
      setEditPrefs(null);
      toast.success("Dashboard aggiornata");
    } catch {
      toast.error("Errore nel salvataggio");
    }
  }, [editPrefs, savePrefs]);

  const handleResetPrefs = useCallback(async () => {
    try {
      await resetPrefs();
      setIsEditMode(false);
      setEditPrefs(null);
      toast.success("Layout ripristinato");
    } catch {
      toast.error("Errore nel ripristino");
    }
  }, [resetPrefs]);

  const hasMultiStrutturaDashboard = (strutture?.length || 0) > 1;
  const [aggregateControlloView, setAggregateControlloView] = useState(() => {
    try {
      return localStorage.getItem(DASHBOARD_AGGREGATE_CONTROLLO_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_AGGREGATE_CONTROLLO_KEY, aggregateControlloView ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [aggregateControlloView]);

  useEffect(() => {
    if (!hasMultiStrutturaDashboard && aggregateControlloView) setAggregateControlloView(false);
  }, [hasMultiStrutturaDashboard, aggregateControlloView]);
  const [gruppiAcquisto, setGruppiAcquisto] = useState(MOCK_GRUPPI_ACQUISTO);
  
  // Ospiti in Rete state
  const [ospitiInRete, setOspitiInRete] = useState({ count: 0, totale_gettone: 0 });
  
  // Newsletter states
  const [newsletters, setNewsletters] = useState([]);
  const [readNewsletters, setReadNewsletters] = useState(() => {
    const saved = localStorage.getItem(READ_NEWSLETTERS_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedNewsletter, setSelectedNewsletter] = useState(null);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  
  // Modal states
  const [showPostoModal, setShowPostoModal] = useState(false);
  const [showOspiteModal, setShowOspiteModal] = useState(false);
  const [showAttivitaModal, setShowAttivitaModal] = useState(false);
  const [showPersonaleModal, setShowPersonaleModal] = useState(false);
  const [showCalendarioModal, setShowCalendarioModal] = useState(false);

  const [dashboardHeroSrc] = useState(() => {
    const list = DASHBOARD_HERO_IMAGES;
    return list[Math.floor(Math.random() * list.length)];
  });

  // Google Calendar state
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);
  const [googleCalendarEmail, setGoogleCalendarEmail] = useState(null);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [eventiCompleanni, setEventiCompleanni] = useState([]);

  const loadPromemoria = useCallback(async () => {
    if (!token) return;
    const [year, month] = selectedMonthKey.split("-").map(Number);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);
    const iso = (value) => value.toISOString().slice(0, 10);
    try {
      const response = await axios.get(`${API_URL}/api/promemoria`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { start: iso(start), end: iso(end), strutturaId: strutturaKey || undefined },
      });
      setAttivita((response.data?.items || []).map((item) => ({
        ...item,
        data: new Date(`${item.occorrenza_data || item.data}T00:00:00`),
        source: "promemoria",
      })));
    } catch (error) {
      console.error("Promemoria fetch error:", error);
      setAttivita([]);
    }
  }, [token, strutturaKey, selectedMonthKey]);

  // Immobiliare alert state
  const [hasNewImmobili, setHasNewImmobili] = useState(false);
  const [immobiliCount, setImmobiliCount] = useState(0);

  // Risparmio ordini state
  const [risparmioData, setRisparmioData] = useState({ risparmio_totale: 0, num_ordini: 0 });
  
  // Promozioni attive state
  const [promoAttive, setPromoAttive] = useState([]);

  // Contatori dinamici per le card
  const [fascicoliStat, setFascicoliStat] = useState({ ospiti: 0, pai: 0, residenti: 0, alerts: 0 });
  const [preIngressoAvvisi, setPreIngressoAvvisi] = useState([]);
  const [alloggiatiWebStat, setAlloggiatiWebStat] = useState({ ospiti: 0, prossimiInvii: [] });
  const [personaleRichieste, setPersonaleRichieste] = useState(0);
  const [personaleRichiesteTotale, setPersonaleRichiesteTotale] = useState(0);
  const [diarioOggiStat, setDiarioOggiStat] = useState({ consegne: 0, segnalazioni: 0 });
  
  // Lead compatibili per Posti Liberi
  const [leadCompatibiliCount, setLeadCompatibiliCount] = useState(0);

  // Alert reinvio Alloggiati Web (ospiti in scadenza)
  const [alertReinvio, setAlertReinvio] = useState({ has_alert: false, count_in_scadenza: 0, count_scaduti: 0 });

  // Piani terapeutici in scadenza (entro 1 mese)
  const [alertPianiTerapeutici, setAlertPianiTerapeutici] = useState([]);
  /** Riepilogo API /deblistering/riepilogo — per KPI card Panoramica dettagliata */
  const [deblisteringRiepilogo, setDeblisteringRiepilogo] = useState(null);

  // Form states
  const [nuovaAttivita, setNuovaAttivita] = useState({
    titolo: "", area: "", ora: "", note: "", categoria: "altro",
    ricorrenza: "nessuna", ogni_n_giorni: "", visibile_oss: true,
  });

  useEffect(() => {
    loadPromemoria();
  }, [loadPromemoria]);

  useEffect(() => {
    const migrateLegacyAttivita = async () => {
      if (!token || localStorage.getItem("affidya_attivita_migrated_v1")) return;
      let legacy = [];
      try {
        legacy = JSON.parse(localStorage.getItem("affidya_attivita") || "[]");
      } catch {
        legacy = [];
      }
      if (!Array.isArray(legacy) || !legacy.length) {
        localStorage.setItem("affidya_attivita_migrated_v1", "1");
        return;
      }
      try {
        await Promise.all(legacy.map((item) => axios.post(`${API_URL}/api/promemoria`, {
          titolo: item.titolo || "Attività",
          note: item.note || "",
          data: new Date(item.data).toISOString().slice(0, 10),
          ora: item.ora || null,
          categoria: "altro",
          ricorrenza: "nessuna",
          visibile_oss: true,
        }, { headers: { Authorization: `Bearer ${token}` } })));
        localStorage.setItem("affidya_attivita_migrated_v1", "1");
        localStorage.removeItem("affidya_attivita");
        await loadPromemoria();
      } catch (error) {
        console.error("Migrazione attività dashboard non riuscita:", error);
      }
    };
    migrateLegacyAttivita();
  }, [token, loadPromemoria]);
  const [editingAttivitaId, setEditingAttivitaId] = useState(null); // ID dell'attività in modifica (null = nuova)
  const [nuovoOspite, setNuovoOspite] = useState({ nome: "", cognome: "", note: "" });
  const [nuovoPosto, setNuovoPosto] = useState({ camera: "", tipo: "", note: "" });
  const [richiestaPersonale, setRichiestaPersonale] = useState({ ruolo: "", urgenza: "Media", orarioInizio: "", orarioFine: "", note: "" });

  // Fetch newsletters (dashboard card Newsletter solo per utenti non beta fatturazione)
  useEffect(() => {
    if (dashboardFatturazione) return;
    const fetchNewsletters = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/newsletters`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNewsletters(response.data?.data || []);
      } catch (error) {
        console.error("Newsletter fetch error:", error);
      }
    };
    fetchNewsletters();
  }, [token, strutturaKey, dashboardFatturazione]);

  // Fetch Ospiti in Rete data
  useEffect(() => {
    const fetchOspitiInRete = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/ospiti-in-rete?struttura=${encodeURIComponent(strutturaDisplayName)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const totali = response.data?.totali || {};
        setOspitiInRete({
          count: response.data?.count || 0,
          totale_gettone: totali.totale_gettone || 0,
          gettone_maturato: totali.gettone_maturato || 0
        });
      } catch (error) {
        console.error("Ospiti in Rete fetch error:", error);
      }
    };
    fetchOspitiInRete();
  }, [token, strutturaKey, strutturaDisplayName]);
  
  // Fetch Lead Compatibili count per card Posti Liberi
  useEffect(() => {
    const fetchLeadCompatibili = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/posti-liberi/lead-compatibili`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLeadCompatibiliCount(response.data.count || 0);
      } catch (error) {
        console.error("Lead compatibili fetch error:", error);
      }
    };
    fetchLeadCompatibili();
  }, [token, strutturaKey]);

  // Fetch Alert Reinvio Alloggiati Web (ospiti da reinviare entro 48h)
  useEffect(() => {
    const fetchAlertReinvio = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/alloggiati-web/alert-reinvio`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAlertReinvio({
          has_alert: response.data.has_alert || false,
          count_in_scadenza: response.data.count_in_scadenza || 0,
          count_scaduti: response.data.count_scaduti || 0,
          ospiti_in_scadenza: response.data.ospiti_in_scadenza || [],
          ospiti_scaduti: response.data.ospiti_scaduti || []
        });
      } catch (error) {
        console.error("Alert reinvio fetch error:", error);
      }
    };
    fetchAlertReinvio();
  }, [token, strutturaKey]);

  // Fetch Risparmio ordini
  useEffect(() => {
    const fetchRisparmio = async () => {
      try {
        const strutturaId = user?.struttura_id || ""; // struttura attiva (effective ID)
        if (!strutturaId) return;
        const response = await axios.get(
          `${API_URL}/api/ordini/risparmio/${strutturaId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRisparmioData(response.data);
      } catch (error) {
        console.error("Risparmio fetch error:", error);
      }
    };
    fetchRisparmio();
  }, [token, strutturaKey, user?.struttura_id]);

  // Fetch Promozioni attive (gruppi con 5+ adesioni)
  useEffect(() => {
    const fetchPromoAttive = async () => {
      try {
        // Carica adesioni totali per gruppo (/all: autenticato utente;
        // /all-with-test è riservato al super admin)
        const adesioniResponse = await axios.get(
          `${API_URL}/api/adesioni-gruppo/all`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Carica listino prodotti
        const priceListResponse = await axios.get(`${API_URL}/api/price-list`);
        
        // Conta adesioni per ogni gruppo (gruppo = prodotto, risolto via SKU)
        const prodotti = priceListResponse.data.products || [];
        const adesioniPerGruppo = countAdesioniByGroup(adesioniResponse.data.adesioni || [], prodotti);
        
        // Trova prodotti con 5+ adesioni
        const MINIMO_STRUTTURE = 5;
        const promoList = prodotti
          .map(p => {
            const gruppoId = productGroupId(p);
            const aderenti = adesioniPerGruppo[gruppoId] || 0;
            return {
              nome: p.name,
              prezzoBase: p.base_price,
              prezzoGruppo: Math.round(p.base_price * 0.62 * 100) / 100,
              sconto: 38,
              aderenti
            };
          })
          .filter(p => p.aderenti >= MINIMO_STRUTTURE);
        
        setPromoAttive(promoList);
      } catch (error) {
        console.error("Promo attive fetch error:", error);
      }
    };
    fetchPromoAttive();
  }, [token, strutturaKey]);

  // Fetch Fascicoli Assistenziali stats
  useEffect(() => {
    const fetchFascicoli = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/fascicoli-assistenziali`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const fascicoli = response.data || [];
        const fascicoliAttivi = fascicoli.filter((c) => !c.archiviata);
        const fascicoliInStruttura = fascicoliAttivi.filter((c) => c.stato_ospite !== "PRE_INGRESSO");
        const alerts = fascicoliAttivi.reduce((acc, c) => acc + (c.alert_attivi || 0), 0);
        const paiAttivi = fascicoliAttivi.filter((c) => c.pai_scadenza).length;
        const residenti = fascicoliAttivi.filter((c) => c.residente_in_struttura === true).length;
        setFascicoliStat({
          ospiti: fascicoliInStruttura.length,
          pai: paiAttivi,
          residenti: residenti,
          alerts: alerts
        });
      } catch (error) {
        console.error("Fascicoli assistenziali fetch error:", error);
      }
    };
    fetchFascicoli();
  }, [token, strutturaKey]);

  useEffect(() => {
    const fetchPreIngressoAvvisi = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/dashboard/pre-ingressi`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPreIngressoAvvisi(response.data?.avvisi || []);
      } catch (error) {
        console.error("Pre-ingresso avvisi fetch error:", error);
        setPreIngressoAvvisi([]);
      }
    };
    if (token) fetchPreIngressoAvvisi();
  }, [token, strutturaKey]);

  // Fetch alert piani terapeutici in scadenza (entro 1 mese)
  useEffect(() => {
    const fetchAlertPianiTerapeutici = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/fascicoli-assistenziali/alert-piani-terapeutici`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAlertPianiTerapeutici(response.data.items || []);
      } catch (error) {
        console.error("Alert piani terapeutici fetch error:", error);
      }
    };
    fetchAlertPianiTerapeutici();
  }, [token, strutturaKey]);

  // Fetch Alloggiati Web stats (da localStorage)
  useEffect(() => {
    const fetchAlloggiatiWeb = () => {
      try {
        const ospiti = JSON.parse(localStorage.getItem('alloggiatiWeb_ospiti') || '[]');
        const prossimiInvii = ospiti
          .filter(o => o.prossimoInvio)
          .map(o => o.prossimoInvio)
          .slice(0, 3);
        setAlloggiatiWebStat({
          ospiti: ospiti.length,
          prossimiInvii: prossimiInvii
        });
      } catch (error) {
        console.error("Alloggiati Web fetch error:", error);
      }
    };
    fetchAlloggiatiWeb();
  }, [strutturaKey]);

  // Fetch Personale richieste attive
  useEffect(() => {
    const fetchRichiestePersonale = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/recruiting/requests`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const richieste = response.data || [];
        setPersonaleRichiesteTotale(richieste.length);
        const attive = richieste.filter(r => r.stato !== 'chiusa' && r.stato !== 'completata').length;
        setPersonaleRichieste(attive);
      } catch (error) {
        // Se l'utente non ha un piano, restituisce 0
        setPersonaleRichieste(0);
        setPersonaleRichiesteTotale(0);
      }
    };
    fetchRichiestePersonale();
  }, [token, strutturaKey]);

  // Riepilogo Deblistering (solo numeri pubblici sulla card panoramica)
  useEffect(() => {
    if (!token) {
      setDeblisteringRiepilogo(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API_URL}/api/deblistering/riepilogo`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) setDeblisteringRiepilogo(res.data);
      })
      .catch(() => {
        if (!cancelled) setDeblisteringRiepilogo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token, strutturaKey]);

  // Fetch Immobiliare count and check for new ones
  useEffect(() => {
    if (!dashboardImmobiliare) return;
    const fetchImmobiliare = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/immobili`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const currentCount = response.data.data?.length || 0;
        setImmobiliCount(currentCount);
        const seenCount = parseInt(localStorage.getItem("affidya_immobiliare_seen_count") || "0", 10);
        if (currentCount > seenCount) {
          setHasNewImmobili(true);
        }
      } catch (error) {
        console.error("Immobiliare fetch error:", error);
      }
    };
    fetchImmobiliare();
  }, [token, strutturaKey, dashboardImmobiliare]);

  // Check Google Calendar connection status and fetch events
  useEffect(() => {
    const checkGoogleCalendar = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/google-calendar/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGoogleCalendarConnected(response.data.connected);
        setGoogleCalendarEmail(response.data.email);
        
        // If connected, fetch events
        if (response.data.connected) {
          const eventsResponse = await axios.get(`${API_URL}/api/google-calendar/events?days=60`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setGoogleEvents(eventsResponse.data.events || []);
        }
      } catch (error) {
        console.error("Google Calendar status error:", error);
      }
    };
    checkGoogleCalendar();
    
    // Check URL params for callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_calendar') === 'connected') {
      toast.success("Google Calendar collegato con successo!");
      window.history.replaceState({}, '', '/');
      checkGoogleCalendar();
    } else if (urlParams.get('google_calendar') === 'error') {
      toast.error("Errore nel collegamento a Google Calendar");
      window.history.replaceState({}, '', '/');
    }
  }, [token, strutturaKey]);

  // Compleanni ospiti (fascicoli) per il calendario — mese corrente e mese successivo
  useEffect(() => {
    const loadCompleanniCalendario = async () => {
      if (!token || !strutturaKey) {
        setEventiCompleanni([]);
        return;
      }
      try {
        const now = new Date();
        const startD = new Date(now.getFullYear(), now.getMonth(), 1);
        const endD = new Date(now.getFullYear(), now.getMonth() + 2, 0);
        const iso = (d) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const response = await axios.get(`${API_URL}/api/dashboard/compleanni-calendario`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { start: iso(startD), end: iso(endD) },
        });
        setEventiCompleanni(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Compleanni calendario fetch error:", error);
        setEventiCompleanni([]);
      }
    };
    loadCompleanniCalendario();
  }, [token, strutturaKey]);

  // Connect Google Calendar
  const connectGoogleCalendar = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/google-calendar/auth`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.authUrl) {
        window.location.href = response.data.authUrl;
      }
    } catch (error) {
      console.error("Google Calendar auth error:", error);
      toast.error("Errore nell'avvio del collegamento Google");
    }
  };

  // Disconnect Google Calendar
  const disconnectGoogleCalendar = async () => {
    try {
      await axios.delete(`${API_URL}/api/google-calendar/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGoogleCalendarConnected(false);
      setGoogleCalendarEmail(null);
      setGoogleEvents([]);
      toast.success("Google Calendar disconnesso");
    } catch (error) {
      toast.error("Errore nella disconnessione");
    }
  };

  const eventiCompleanniMapped = useMemo(
    () =>
      (eventiCompleanni || []).map((e) => ({
        id: e.id,
        titolo: e.titolo,
        area: e.area || "",
        ora: e.ora || "",
        data: new Date(e.data),
        note: "",
        source: "compleanno",
      })),
    [eventiCompleanni]
  );

  // Merge promemoria condivisi, compleanni ospiti e Google Calendar
  const allAttivita = useMemo(
    () => [
      ...attivita,
      ...eventiCompleanniMapped,
      ...googleEvents.map((e) => ({
        id: e.id,
        titolo: e.title,
        area: e.location || "",
        ora: e.start
          ? new Date(e.start).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
          : "",
        data: e.start ? new Date(e.start) : new Date(),
        source: "google",
      })),
    ],
    [attivita, eventiCompleanniMapped, googleEvents]
  );

  // Count unread newsletters
  const unreadCount = newsletters.filter(n => !readNewsletters.includes(n.id)).length;

  // Mark newsletter as read
  const markAsRead = (newsletterId) => {
    if (!readNewsletters.includes(newsletterId)) {
      const updated = [...readNewsletters, newsletterId];
      setReadNewsletters(updated);
      localStorage.setItem(READ_NEWSLETTERS_KEY, JSON.stringify(updated));
    }
  };

  // Open newsletter with PDF viewer
  const openNewsletter = (newsletter) => {
    setSelectedNewsletter(newsletter);
    setShowNewsletterModal(true);
    markAsRead(newsletter.id);
  };

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setData(response.data);
      } catch (error) {
        console.error("Dashboard fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [token, strutturaKey]);

  useEffect(() => {
    const fetchDiarioOggi = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/diario-consegne/stats-oggi`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDiarioOggiStat({
          consegne: response.data.consegne_oggi ?? 0,
          segnalazioni: response.data.segnalazioni_oggi ?? 0,
        });
      } catch {
        setDiarioOggiStat({ consegne: 0, segnalazioni: 0 });
      }
    };
    if (token) fetchDiarioOggi();
  }, [token, strutturaKey]);

  const handleAggiungiAttivita = async () => {
    if (!nuovaAttivita.titolo) {
      toast.error("Inserisci il titolo dell'attività");
      return;
    }
    const payload = {
      titolo: nuovaAttivita.titolo,
      note: nuovaAttivita.note,
      data: selectedDate.toISOString().slice(0, 10),
      ora: nuovaAttivita.ora || null,
      categoria: nuovaAttivita.categoria,
      ricorrenza: nuovaAttivita.ricorrenza,
      ogni_n_giorni: nuovaAttivita.ricorrenza === "ogni_n_giorni"
        ? Number(nuovaAttivita.ogni_n_giorni) || null : null,
      visibile_oss: nuovaAttivita.visibile_oss,
    };
    try {
      if (editingAttivitaId) {
        await axios.patch(`${API_URL}/api/promemoria/${editingAttivitaId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Promemoria aggiornato");
      } else {
        await axios.post(`${API_URL}/api/promemoria`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Promemoria aggiunto");
      }
      await loadPromemoria();
      setNuovaAttivita({
        titolo: "", area: "", ora: "", note: "", categoria: "altro",
        ricorrenza: "nessuna", ogni_n_giorni: "", visibile_oss: true,
      });
      setEditingAttivitaId(null);
      setShowAttivitaModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile salvare il promemoria");
    }
  };
  
  // Funzione per aprire il modal in modalità modifica
  const handleEditAttivita = (att) => {
    if (att.source === "compleanno" || att.source === "google") {
      toast.info("Questo evento non può essere modificato dall'agenda.");
      return;
    }
    setNuovaAttivita({
      titolo: att.titolo,
      area: att.area || "",
      ora: att.ora || "",
      note: att.note || "",
      categoria: att.categoria || "altro",
      ricorrenza: att.ricorrenza || "nessuna",
      ogni_n_giorni: att.ogni_n_giorni || "",
      visibile_oss: att.visibile_oss !== false,
    });
    setEditingAttivitaId(att.id);
    setSelectedDate(att.data);
    setShowCalendarioModal(false);
    setShowAttivitaModal(true);
  };
  
  // Funzione per eliminare un'attività
  const handleDeleteAttivita = async (attivitaId) => {
    const target = allAttivita.find((a) => a.id === attivitaId);
    if (target?.source === "compleanno" || target?.source === "google") {
      toast.info("Questo evento non può essere eliminato dall'agenda.");
      return;
    }
    try {
      await axios.delete(`${API_URL}/api/promemoria/${attivitaId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadPromemoria();
      toast.success("Promemoria eliminato");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossibile eliminare il promemoria");
    }
  };

  const handleSegnalaPosto = () => {
    if (!nuovoPosto.camera) {
      toast.error("Inserisci il numero camera");
      return;
    }
    setShowPostoModal(false);
    setNuovoPosto({ camera: "", tipo: "", note: "" });
    toast.success("Posto libero segnalato!");
  };

  const handleInviaOspite = () => {
    if (!nuovoOspite.nome || !nuovoOspite.cognome) {
      toast.error("Inserisci nome e cognome");
      return;
    }
    setShowOspiteModal(false);
    setNuovoOspite({ nome: "", cognome: "", note: "" });
    toast.success("Ospite inviato alla rete!");
  };

  const handleRichiestaPersonale = () => {
    if (!richiestaPersonale.ruolo) {
      toast.error("Seleziona il ruolo richiesto");
      return;
    }
    setShowPersonaleModal(false);
    setRichiestaPersonale({ ruolo: "", urgenza: "Media", orarioInizio: "", orarioFine: "", note: "" });
    toast.success("Richiesta personale inviata!");
  };

  const handleAderisci = (id) => {
    setGruppiAcquisto(gruppiAcquisto.map(g => {
      if (g.id === id) {
        const nuoviAderenti = g.aderenti + 1;
        const attivo = nuoviAderenti >= g.minimo;
        if (attivo && !g.attivo) {
          toast.success(`Promo attivata per ${g.nome}!`);
        }
        return { ...g, aderenti: nuoviAderenti, attivo };
      }
      return g;
    }));
    toast.success("Hai aderito al gruppo di acquisto!");
  };

  // dnd-kit sensors for sortable grids
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Filtra attività per data selezionata (incl. Google e compleanni)
  const attivitaFiltrate = allAttivita.filter((a) => a.data.toDateString() === selectedDate.toDateString());

  // Attività di oggi (per il widget)
  const todayDate = new Date();
  const attivitaOggi = allAttivita.filter((a) => a.data.toDateString() === todayDate.toDateString());

  // Date con attività
  const dateConAttivita = allAttivita.map((a) => a.data);

  const oggi = new Date();
  const giorni = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"];
  const mesi = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
  const dataFormattata = `${giorni[oggi.getDay()]} ${oggi.getDate()} ${mesi[oggi.getMonth()]}`;
  const dashboardSubtitle = isAdvancedMode
    ? aggregateControlloView && hasMultiStrutturaDashboard
      ? "Panoramica aggregata delle strutture selezionate"
      : "Ecco la panoramica completa della tua struttura"
    : "Gestisci e monitora le attività quotidiane";
  const dashboardHeader = (
    <PageHeader
      title={`${getSaluto()}, ${strutturaDisplayName}`}
      subtitle={dashboardSubtitle}
    />
  );

  if (loading) {
    return (
      <>
        {dashboardHeader}
        <div className="p-4 md:p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-48 bg-slate-200 rounded-xl" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 bg-slate-200 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-full bg-gradient-to-b from-[#F1F3F5] via-[#ECEFF2] to-[#E6EAEE]">
      {dashboardHeader}
      <div className="relative w-full overflow-hidden bg-[#F1F3F5]">
        {/* Illustrazione a destra (una variante random tra quattro): object-cover object-right-bottom. */}
        <div
          className="absolute right-0 top-0 h-full w-[44%] sm:w-[48%] md:w-[52%] lg:w-[56%] max-w-[820px] pointer-events-none select-none overflow-hidden"
          style={{
            WebkitMaskImage:
              'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 22%, #000 36%), linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 12%, rgba(0,0,0,0.9) 26%, #000 38%)',
            maskImage:
              'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 22%, #000 36%), linear-gradient(to right, transparent 0%, rgba(0,0,0,0.4) 12%, rgba(0,0,0,0.9) 26%, #000 38%)',
            WebkitMaskComposite: 'source-in',
            maskComposite: 'intersect',
          }}
          aria-hidden="true"
        >
          <div className="relative h-full min-h-[200px] w-full">
            <img
              src={dashboardHeroSrc}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-right-bottom"
            />
          </div>
        </div>

        <div className={`relative z-10 ${DASHBOARD_MAX_WIDTH_CLASS} px-4 sm:px-6 md:px-8 xl:px-10 py-3`}>
          <ReleaseNotesBanner />
          <div className="flex items-center gap-2 text-slate-700">
            <CalendarDays className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium">{dataFormattata}</span>
          </div>

          {/* Selettore vista Panoramica / Controllo completo */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-1.5 bg-white/70 backdrop-blur rounded-full ring-1 ring-slate-200 shadow-sm px-1.5 py-1.5">
              <span className="hidden sm:inline text-[11px] font-medium text-slate-500 whitespace-nowrap pl-2 pr-1">Vista:</span>
              <button
                onClick={() => handleToggleDashboardMode(false)}
                className={`text-xs font-semibold whitespace-nowrap px-3 py-1 rounded-full transition-all ${
                  !isAdvancedMode
                    ? "bg-white text-[#16a34a] ring-1 ring-[#16a34a] shadow-sm"
                    : "text-slate-400 hover:text-slate-600"
                }`}
                data-testid="dashboard-mode-panoramica"
              >
                Panoramica
              </button>
              <button
                onClick={() => handleToggleDashboardMode(true)}
                className={`text-xs font-semibold whitespace-nowrap px-3 py-1 rounded-full transition-all ${
                  isAdvancedMode
                    ? "bg-white text-[#16a34a] ring-1 ring-[#16a34a] shadow-sm"
                    : "text-slate-400 hover:text-slate-600"
                }`}
                data-testid="dashboard-mode-completo"
              >
                Controllo completo
              </button>
            </div>
            {!isEditMode && !(isAdvancedMode && aggregateControlloView) && (
              <button
                onClick={enterEditMode}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 hover:text-slate-700 bg-white/70 backdrop-blur rounded-full ring-1 ring-slate-200 shadow-sm px-3 py-1.5 transition-all hover:bg-white"
                title="Personalizza la dashboard"
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Personalizza</span>
              </button>
            )}
          </div>

          {isAdvancedMode && hasMultiStrutturaDashboard && (
            <div className="mt-2 flex flex-wrap gap-2">
              <div className="inline-flex items-center gap-1.5 bg-white/70 backdrop-blur rounded-full ring-1 ring-slate-200 shadow-sm px-1.5 py-1.5">
                <span className="hidden sm:inline text-[11px] font-medium text-slate-500 whitespace-nowrap pl-2 pr-1">
                  Multi-struttura:
                </span>
                <button
                  type="button"
                  onClick={() => setAggregateControlloView(false)}
                  className={`text-xs font-semibold whitespace-nowrap px-3 py-1 rounded-full transition-all ${
                    !aggregateControlloView
                      ? "bg-white text-[#16a34a] ring-1 ring-[#16a34a] shadow-sm"
                      : "text-slate-400 hover:text-slate-600"
                  }`}
                  data-testid="dashboard-controllo-struttura-corrente"
                >
                  Struttura corrente
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAggregateControlloView(true);
                    if (isEditMode) cancelEditMode();
                  }}
                  className={`text-xs font-semibold whitespace-nowrap px-3 py-1 rounded-full transition-all ${
                    aggregateControlloView
                      ? "bg-white text-[#16a34a] ring-1 ring-[#16a34a] shadow-sm"
                      : "text-slate-400 hover:text-slate-600"
                  }`}
                  data-testid="dashboard-controllo-vista-aggregata"
                >
                  Vista aggregata
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={`p-3 sm:p-4 md:p-6 lg:p-8 xl:px-10 2xl:px-12 ${DASHBOARD_MAX_WIDTH_CLASS}`}>

        {/* Modal Calendario */}
        <CalendarioModal
          isOpen={showCalendarioModal}
          onClose={() => {
            setShowCalendarioModal(false);
            setSelectedDate(new Date());
          }}
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          attivitaFiltrate={attivitaFiltrate}
          allAttivita={allAttivita}
          onAddAttivita={() => {
            setEditingAttivitaId(null);
            setNuovaAttivita({ titolo: "", area: "", ora: "", note: "" });
            setShowCalendarioModal(false);
            setShowAttivitaModal(true);
          }}
          onEditAttivita={handleEditAttivita}
          onDeleteAttivita={handleDeleteAttivita}
          googleCalendarConnected={googleCalendarConnected}
          googleCalendarEmail={googleCalendarEmail}
          onConnectGoogle={connectGoogleCalendar}
          onDisconnectGoogle={disconnectGoogleCalendar}
        />

        {isEditMode && (
          <div className="sticky top-0 z-40 -mx-3 sm:-mx-4 md:-mx-6 lg:-mx-8 xl:-mx-10 2xl:-mx-12 px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12 py-2.5 mb-4 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="w-4 h-4 text-slate-600" />
                <span className="text-sm font-semibold text-slate-700">Modifica layout</span>
                <span className="hidden sm:inline text-xs text-slate-400">
                  {isAdvancedMode && !aggregateControlloView
                    ? "Trascina i blocchi · scegli la tipologia · mostra/nascondi"
                    : "Trascina per riordinare · seleziona visibilità e dimensione"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleResetPrefs}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-300 bg-white transition-all"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Ripristina
                </button>
                <button
                  onClick={cancelEditMode}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-300 bg-white transition-all"
                >
                  <X className="w-3.5 h-3.5" />
                  Annulla
                </button>
                <button
                  onClick={confirmEditMode}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-4 py-1.5 rounded-full shadow-sm transition-all"
                >
                  <Check className="w-3.5 h-3.5" />
                  Salva
                </button>
              </div>
            </div>
          </div>
        )}

        {!isEditMode && preIngressoAvvisi.length > 0 && (
          <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
              Ingresso previsto oggi ma pratica incompleta
            </p>
            <ul className="mt-2 space-y-1 text-amber-800">
              {preIngressoAvvisi.map((a) => (
                <li key={a.fascicolo_id}>
                  <Link
                    to={`/fascicoli-assistenziali/${a.fascicolo_id}`}
                    className="underline hover:text-amber-950"
                  >
                    {a.nome_completo}
                  </Link>
                  : {a.messaggio}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!isEditMode && (
          <div className="sticky top-0 z-40 -mx-3 sm:-mx-4 md:-mx-6 lg:-mx-8 xl:-mx-10 2xl:-mx-12 px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12 pt-1 pb-3 mb-2 bg-[#ECEFF2]">
            <PanoramicaQuickActionsBar />
          </div>
        )}

        {isAdvancedMode ? (
          <DashboardAdvanced
            data={data}
            dashboardFatturazione={dashboardFatturazione}
            aggregateControlloView={aggregateControlloView}
            hasMultiStruttura={hasMultiStrutturaDashboard}
            prefControllo={(isEditMode && !aggregateControlloView ? editPrefs : prefs)?.controllo ?? []}
            isCustomizeMode={isEditMode && !aggregateControlloView}
            setDraftPrefs={setEditPrefs}
          />
        ) : (() => {
          // All data the card renderers need, keyed by card id
          const cardData = {
            // calendario
            selectedDate,
            attivitaOggi,
            allAttivita,
            onOpenFullCalendar: () => setShowCalendarioModal(true),
            googleCalendarConnected,
            eventiCompleanniCount: eventiCompleanni?.length ?? 0,
            // fascicoli
            fascicoliStat,
            alertPianiTerapeuticiCount: alertPianiTerapeutici?.length ?? 0,
            // alloggiati
            alertReinvio,
            alloggiatiWebStat,
            // posti-liberi
            postiLiberi: data?.kpi?.posti_liberi ?? 0,
            leadCompatibiliCount,
            // ospiti-rete
            ospitiInRete,
            // personale
            personaleRichieste,
            personaleRichiesteTotale,
            // diario-consegne
            diarioOggiStat,
            // immobiliare
            hasNewImmobili,
            immobiliCount,
            kpi: data?.kpi,
            // newsletter
            unreadCount,
            newslettersTotal: newsletters.length,
            // fatturazione
            strutturaDisplayName,
            // risparmio
            risparmioData,
            promoAttive,
            // deblistering
            deblisteringRiepilogo,
          };

          // Use editPrefs when in edit mode, otherwise use saved prefs
          const activePrefs = isEditMode ? editPrefs : prefs;

          // Resolve fatturazione/newsletter mutual exclusion:
          // "fatturazione" is only rendered when dashboardFatturazione is true,
          // "newsletter" only when it is false. The other is simply skipped.
          const resolveCardId = (id) => {
            if (id === "fatturazione" && !dashboardFatturazione) return null;
            if (id === "newsletter" && dashboardFatturazione) return null;
            if (id === "immobiliare" && !dashboardImmobiliare) return null;
            return id;
          };

          const detailedCards = activePrefs.panoramica.filter(
            (c) => c.size === "detailed" && (isEditMode || c.visible) && resolveCardId(c.id)
          );
          const compactCards = activePrefs.panoramica.filter(
            (c) => c.size === "compact" && (isEditMode || c.visible) && resolveCardId(c.id)
          );

          // Drag end handler — reorders within the section
          const handleDragEnd = (event) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            setEditPrefs((prev) => {
              const list = [...prev.panoramica];
              const oldIdx = list.findIndex((c) => c.id === active.id);
              const newIdx = list.findIndex((c) => c.id === over.id);
              if (oldIdx === -1 || newIdx === -1) return prev;
              return { ...prev, panoramica: arrayMove(list, oldIdx, newIdx) };
            });
          };

          // Toggle visibility
          const toggleVisible = (id) => {
            setEditPrefs((prev) => ({
              ...prev,
              panoramica: prev.panoramica.map((c) =>
                c.id === id ? { ...c, visible: !c.visible } : c
              ),
            }));
          };

          // Toggle size — moves card to end of target section
          const toggleSize = (id, newSize) => {
            setEditPrefs((prev) => {
              const card = prev.panoramica.find((c) => c.id === id);
              if (!card || card.size === newSize) return prev;
              const rest = prev.panoramica.filter((c) => c.id !== id);
              return { ...prev, panoramica: [...rest, { ...card, size: newSize }] };
            });
          };

          return (
            <>
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                {/* Section: Detailed cards */}
                {(detailedCards.length > 0 || isEditMode) && (
                  <div className="mb-3 md:mb-4">
                    {isEditMode && (
                      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <LayoutGrid className="w-3.5 h-3.5" />
                        Vista dettagliata
                      </p>
                    )}
                    <SortableContext
                      items={activePrefs.panoramica.filter((c) => c.size === "detailed").map((c) => c.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 items-stretch">
                        {activePrefs.panoramica
                          .filter((c) => c.size === "detailed" && resolveCardId(c.id))
                          .map((card) => (
                            <SortableDashboardCard
                              key={card.id}
                              card={card}
                              cardData={cardData}
                              isEditMode={isEditMode}
                              onToggleVisible={() => toggleVisible(card.id)}
                              onToggleSize={(s) => toggleSize(card.id, s)}
                            />
                          ))}
                      </div>
                    </SortableContext>
                  </div>
                )}

                {/* Section: Compact cards */}
                {(compactCards.length > 0 || isEditMode) && (
                  <div className="mt-3 md:mt-4">
                    {isEditMode && (
                      <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <Minus className="w-3.5 h-3.5" />
                        Vista compatta
                      </p>
                    )}
                    <SortableContext
                      items={activePrefs.panoramica.filter((c) => c.size === "compact").map((c) => c.id)}
                      strategy={rectSortingStrategy}
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 md:gap-x-4 gap-y-4 md:gap-y-5">
                        {activePrefs.panoramica
                          .filter((c) => c.size === "compact" && resolveCardId(c.id))
                          .map((card) => (
                            <SortableDashboardCard
                              key={card.id}
                              card={card}
                              cardData={cardData}
                              isEditMode={isEditMode}
                              onToggleVisible={() => toggleVisible(card.id)}
                              onToggleSize={(s) => toggleSize(card.id, s)}
                            />
                          ))}
                      </div>
                    </SortableContext>
                  </div>
                )}
              </DndContext>
            </>
          );
        })()}

      </div>

      {/* Newsletter Modal with PDF/Image Viewer */}
      {!dashboardFatturazione && (
      <Dialog open={showNewsletterModal} onOpenChange={setShowNewsletterModal}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          {selectedNewsletter && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">
                  {selectedNewsletter.nome}
                </DialogTitle>
                <p className="text-slate-500 text-sm">
                  {(selectedNewsletter.data_newsletter || selectedNewsletter.data)
                    ? new Date(selectedNewsletter.data_newsletter || selectedNewsletter.data).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  }) : ''}
                </p>
              </DialogHeader>
              
              {(selectedNewsletter.has_file || selectedNewsletter.file_name || selectedNewsletter.file_id) ? (
                <div className="mt-4 space-y-3">
                  {/* Open in new tab button */}
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => window.open(
                        `${API_URL}/api/newsletters/${selectedNewsletter.id}/file`,
                        '_blank'
                      )}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Apri in nuova scheda
                    </Button>
                  </div>
                  {/* Content viewer - immagine o PDF */}
                  <div className="rounded-xl overflow-hidden border bg-slate-100">
                    <img
                      src={`${API_URL}/api/newsletters/${selectedNewsletter.id}/file`}
                      alt={selectedNewsletter.nome}
                      className="w-full max-h-[55vh] object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <object
                      data={`${API_URL}/api/newsletters/${selectedNewsletter.id}/file`}
                      type="application/pdf"
                      className="w-full h-[55vh]"
                      style={{ display: 'none' }}
                    >
                      <div className="p-8 text-center">
                        <FileText className="w-16 h-16 text-purple-500 mx-auto mb-4" />
                        <p className="text-slate-700 font-medium mb-4">
                          Clicca per visualizzare il contenuto
                        </p>
                        <Button
                          className="bg-purple-600 hover:bg-purple-700"
                          onClick={() => window.open(
                            `${API_URL}/api/newsletters/${selectedNewsletter.id}/file`,
                            '_blank'
                          )}
                        >
                          Scarica PDF
                        </Button>
                      </div>
                    </object>
                  </div>
                </div>
              ) : (
                <div className="mt-4 p-12 bg-slate-100 rounded-xl text-center">
                  <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <p className="text-slate-500">Nessun documento allegato</p>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewsletterModal(false)}>
                  Chiudi
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      )}

      {/* Modals */}
      {/* Modal Nuova/Modifica Attività */}
      <Dialog open={showAttivitaModal} onOpenChange={(open) => {
        setShowAttivitaModal(open);
        if (!open) {
          // Reset form quando si chiude
          setEditingAttivitaId(null);
          setNuovaAttivita({
            titolo: "", area: "", ora: "", note: "", categoria: "altro",
            ricorrenza: "nessuna", ogni_n_giorni: "", visibile_oss: true,
          });
        }
      }}>
        <DialogContent className="max-w-[95vw] sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingAttivitaId ? "Modifica Attività" : "Nuova Attività"}</DialogTitle>
            <DialogDescription>
              {editingAttivitaId ? "Modifica l'" : "Crea un'"}attività per il {selectedDate.toLocaleDateString('it-IT')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titolo *</Label>
              <Input
                value={nuovaAttivita.titolo}
                onChange={(e) => setNuovaAttivita({...nuovaAttivita, titolo: e.target.value})}
                placeholder="Es. Visita medica"
                data-testid="input-attivita-titolo"
              />
            </div>
            <div className="space-y-2">
              <Label>Orario</Label>
              {/* Pulsanti rapidi orario */}
              <div className="flex flex-wrap gap-1.5 mb-2">
                {['08:00', '09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00', '18:00'].map((ora) => (
                  <button
                    key={ora}
                    type="button"
                    onClick={() => setNuovaAttivita({...nuovaAttivita, ora})}
                    className={`px-2.5 py-1 text-xs rounded-full border transition-all ${
                      nuovaAttivita.ora === ora 
                        ? 'bg-amber-500 text-white border-amber-500' 
                        : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400 hover:bg-amber-50'
                    }`}
                  >
                    {ora}
                  </button>
                ))}
              </div>
              {/* Input manuale per orari personalizzati */}
              <Input
                type="text"
                placeholder="Orario personalizzato (es: 13:45)"
                value={nuovaAttivita.ora}
                onChange={(e) => setNuovaAttivita({...nuovaAttivita, ora: e.target.value})}
                data-testid="input-attivita-ora"
                className="text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo promemoria</Label>
              <Select
                value={nuovaAttivita.categoria}
                onValueChange={(categoria) => setNuovaAttivita({ ...nuovaAttivita, categoria })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="catetere">Cambio catetere</SelectItem>
                  <SelectItem value="prelievo">Prelievo</SelectItem>
                  <SelectItem value="visita">Visita o appuntamento</SelectItem>
                  <SelectItem value="digiuno">Digiuno</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Ripetizione</Label>
              <Select
                value={nuovaAttivita.ricorrenza}
                onValueChange={(ricorrenza) => setNuovaAttivita({ ...nuovaAttivita, ricorrenza })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nessuna">Una sola volta</SelectItem>
                  <SelectItem value="giornaliera">Ogni giorno</SelectItem>
                  <SelectItem value="settimanale">Ogni settimana</SelectItem>
                  <SelectItem value="mensile">Ogni mese</SelectItem>
                  <SelectItem value="ogni_n_giorni">Ogni N giorni</SelectItem>
                </SelectContent>
              </Select>
              {nuovaAttivita.ricorrenza === "ogni_n_giorni" && (
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={nuovaAttivita.ogni_n_giorni}
                  onChange={(event) => setNuovaAttivita({ ...nuovaAttivita, ogni_n_giorni: event.target.value })}
                  placeholder="Intervallo in giorni"
                />
              )}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={nuovaAttivita.visibile_oss}
                onChange={(event) => setNuovaAttivita({ ...nuovaAttivita, visibile_oss: event.target.checked })}
              />
              Visibile agli OSS nella dashboard di oggi
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAttivitaModal(false)}>Annulla</Button>
            <Button onClick={handleAggiungiAttivita} className="bg-[#4CAF50] hover:bg-[#43A047]" data-testid="btn-salva-attivita">
              {editingAttivitaId ? "Salva Modifiche" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Segnala Posto */}
      <Dialog open={showPostoModal} onOpenChange={setShowPostoModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Segnala Posto Libero</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Numero Camera *</Label>
              <Input
                value={nuovoPosto.camera}
                onChange={(e) => setNuovoPosto({...nuovoPosto, camera: e.target.value})}
                placeholder="Es. 101"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipologia</Label>
              <Select value={nuovoPosto.tipo} onValueChange={(v) => setNuovoPosto({...nuovoPosto, tipo: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="singola">Singola</SelectItem>
                  <SelectItem value="doppia">Doppia</SelectItem>
                  <SelectItem value="tripla">Tripla</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={nuovoPosto.note}
                onChange={(e) => setNuovoPosto({...nuovoPosto, note: e.target.value})}
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPostoModal(false)}>Annulla</Button>
            <Button onClick={handleSegnalaPosto} className="bg-emerald-600 hover:bg-emerald-700">
              Segnala
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Invia Ospite */}
      <Dialog open={showOspiteModal} onOpenChange={setShowOspiteModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Invia Ospite alla Rete</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={nuovoOspite.nome}
                onChange={(e) => setNuovoOspite({...nuovoOspite, nome: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Cognome *</Label>
              <Input
                value={nuovoOspite.cognome}
                onChange={(e) => setNuovoOspite({...nuovoOspite, cognome: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={nuovoOspite.note}
                onChange={(e) => setNuovoOspite({...nuovoOspite, note: e.target.value})}
                placeholder="Profilo assistenziale, zona..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOspiteModal(false)}>Annulla</Button>
            <Button onClick={handleInviaOspite} className="bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-2" />
              Invia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Richiesta Personale */}
      <Dialog open={showPersonaleModal} onOpenChange={setShowPersonaleModal}>
        <DialogContent className="max-w-[95vw] sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Richiesta Personale</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Ruolo richiesto *</Label>
              <Select value={richiestaPersonale.ruolo} onValueChange={(v) => setRichiestaPersonale({...richiestaPersonale, ruolo: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona ruolo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="OSS">OSS</SelectItem>
                  <SelectItem value="Infermiere">Infermiere</SelectItem>
                  <SelectItem value="Fisioterapista">Fisioterapista</SelectItem>
                  <SelectItem value="Animatore">Animatore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Orario inizio</Label>
                <Input
                  type="time"
                  value={richiestaPersonale.orarioInizio}
                  onChange={(e) => setRichiestaPersonale({...richiestaPersonale, orarioInizio: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Orario fine</Label>
                <Input
                  type="time"
                  value={richiestaPersonale.orarioFine}
                  onChange={(e) => setRichiestaPersonale({...richiestaPersonale, orarioFine: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={richiestaPersonale.note}
                onChange={(e) => setRichiestaPersonale({...richiestaPersonale, note: e.target.value})}
                placeholder="Requisiti specifici..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPersonaleModal(false)}>Annulla</Button>
            <Button onClick={handleRichiestaPersonale} className="bg-amber-500 hover:bg-amber-600">
              Invia richiesta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <div className="text-center py-6 mt-8 border-t border-slate-200">
        <p className="text-xs text-slate-400">
          Powered by <a href="https://rco-group.it" target="_blank" rel="noopener noreferrer" className="text-[#3B5998] hover:underline">rco-group.it</a>
          <span className="mx-2">•</span>
          <span className="text-slate-300">Ver. 2</span>
        </p>
      </div>
    </div>
  );
}
